export class ApiService {
  constructor(baseUrl = 'http://localhost:5272', apiKey = '') {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey || process.env.REACT_APP_API_KEY || 'dev-api-key-12345';
  }

  async fetchWithErrorHandling(url, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: Invalid or missing API key');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'An unknown error occurred',
        errors: []
      };
    }
  }

  async createSession(userId, title) {
    return this.fetchWithErrorHandling('/api/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ userId, title }),
    });
  }

  async getSession(sessionId) {
    return this.fetchWithErrorHandling(`/api/chat/sessions/${sessionId}`);
  }

  async getUserSessions(userId) {
    return this.fetchWithErrorHandling(`/api/chat/sessions?userId=${encodeURIComponent(userId)}`);
  }

  async deleteSession(sessionId) {
    return this.fetchWithErrorHandling(`/api/chat/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async uploadDocument(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${this.baseUrl}/api/documents/upload`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.apiKey,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: Invalid or missing API key');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Document upload failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Document upload failed',
        errors: []
      };
    }
  }

  async getDocuments() {
    return this.fetchWithErrorHandling('/api/documents');
  }

  async requestQuote({ customerName, email, companyName = '', phone = '', message = '', requestedPlan = 'Plus' }) {
    const invoicesApiUrl = process.env.REACT_APP_INVOICES_API_URL || 'http://localhost:5249';
    try {
      const response = await fetch(`${invoicesApiUrl}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerName, email, companyName, phone, message, requestedPlan }),
      });
      if (response.status === 202 || response.ok) {
        return { success: true, ...(await response.json().catch(() => ({}))) };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, message: data?.error || 'No se pudo enviar la cotización.' };
    } catch (error) {
      return { success: false, message: 'Error de red. Por favor intenta de nuevo.' };
    }
  }

  async checkHealth() {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return await response.json();
    } catch (error) {
      throw new Error('Health check failed');
    }
  }

  // PDF Methods
  getPdfUrl(filename) {
    return `${this.baseUrl}/api/document/pdf/${encodeURIComponent(filename)}`;
  }

  async downloadPdf(filename) {
    try {
      const response = await fetch(this.getPdfUrl(filename), {
        headers: {
          'X-API-Key': this.apiKey,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: Invalid or missing API key');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('PDF download failed:', error);
      throw error;
    }
  }
}
